import { useDispatch } from 'react-redux';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import ironLegacyClient from '@app/data/ironLegacyClient';
import { setCustomer } from '@app/redux/customer/actions';
import { useCustomer } from '@app/user';
import useErrorMessage from '@app/hooks/useErrorMessage';
import { Customer } from '@app/types';

interface CustomerResponse {
  body: {
    Customer?: Customer;
  };
}

interface UseUpdateCustomerArgs {
  setError: Function;
  setLoading: Function;
}

const useUpdateCustomer = ({ setError, setLoading }: UseUpdateCustomerArgs) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const getErrorMessage = useErrorMessage();
  const initialCustomer = useCustomer();

  const updateCustomer = useCallback(
    async (
      id: string | null | undefined,
      data: Dict,
      success: Function = () => null,
    ) => {
      if (!id) {
        return;
      }

      try {
        if (setLoading) {
          setLoading(true);
        }

        const result = await ironLegacyClient.updateCustomer<CustomerResponse>(
          id,
          data,
        );

        const customer = result.body?.Customer;

        if (customer) {
          dispatch(setCustomer(customer));
        }

        if (setLoading) {
          setLoading(false);
        }

        if (success) {
          success();
        }
      } catch (e) {
        if (setLoading) {
          setLoading(false);
        }

        if (setError) {
          setError(getErrorMessage(e));
        }
      }
    },
    [ironLegacyClient, dispatch, setCustomer, setError, getErrorMessage], // eslint-disable-line react-hooks/exhaustive-deps
  );

  const submitMerchantUpdate = useCallback(
    async (
      id: string | undefined,
      data: Dict,
      success: Function = () => null,
      vaultTraceId: Nullable<string> | undefined = null,
      error: Function = () => null,
    ) => {
      setError(null);
      setLoading(true);

      try {
        const result =
          await ironLegacyClient.postMerchantUpdate<CustomerResponse>(
            id,
            data,
            vaultTraceId,
          );

        const { Customer: customer } = result.body;
        dispatch(
          setCustomer({
            ...initialCustomer,
            ...customer,
          }),
        );
        setLoading(false);
        success(data?.moneyServiceSessionId);
      } catch (e: any) {
        if (e?.response?.statusCode === 500) {
          setError(t('There was an issue. Please review and try again'));
        } else {
          setError(
            e?.response?.body?.message || t('default_error_reverify_messages'),
          );
        }

        error(data?.moneyServiceSessionId);
        setLoading(false);
      }
    },
    [], // eslint-disable-line react-hooks/exhaustive-deps
  );

  return {
    submitMerchantUpdate,
    updateCustomer,
  };
};

export default useUpdateCustomer;
